import { ErrorMessage } from "@/components/common/Error.tsx";
import {
  MainContentWrapper,
  SiteHeaderContent,
  ViewWrapper,
} from "@/components/layout/ViewWrapper.tsx";
import type { ListArtifactsResp } from "@/types/artifacts.ts";
import { useCallback, useState } from "react";
import { useParams, useSearch } from "wouter"; // Using wouter hooks
import { z } from "zod";
import { MessageArtifactList } from "../components/artifacts/MessageArtifactList.tsx";

// Schema for route parameters (threadId, messageId)
const routeParamsSchema = z.object({
  threadId: z.coerce.number(),
  messageId: z.coerce.number(),
});

// Schema for search parameters (domainId, publisherId)
const searchParamsSchema = z.object({
  domainId: z.string().optional(),
  publisherId: z.string().optional(),
});

/**
 * A page view that displays a list of artifacts associated with a specific message.
 * Retrieves threadId/messageId from the route and optional domainId/publisherId from search params.
 */
export const MessageArtifactsScreen = () => {
  const routeParams = useParams();
  const searchString = useSearch();
  const searchParams = new URLSearchParams(searchString);

  // State for the title, derived from loaded tasks
  const [title, setTitle] = useState<string | undefined>("");

  const routeValidation = routeParamsSchema.safeParse(routeParams);
  const searchValidation = searchParamsSchema.safeParse({
    domainId: searchParams.get("domainId") ?? undefined,
    publisherId: searchParams.get("publisherId") ?? undefined,
  });

  if (!routeValidation.success) {
    console.error("Invalid route parameters:", routeValidation.error.flatten());
    const error = new Error(
      `Invalid route parameters. ${routeValidation.error.flatten().formErrors.join(" ")}`,
    );
    return (
      <ViewWrapper headerContent={<SiteHeaderContent title="Error" includeBackButton />}>
        <ErrorMessage error={error} />
      </ViewWrapper>
    );
  }
  if (!searchValidation.success) {
    console.error("Invalid search parameters:", searchValidation.error.flatten());
    const error = new Error(
      `Invalid search parameters. ${searchValidation.error.flatten().formErrors.join(" ")}`,
    );
    return (
      <ViewWrapper headerContent={<SiteHeaderContent title="Error" includeBackButton />}>
        <ErrorMessage error={error} />
      </ViewWrapper>
    );
  }

  const { threadId, messageId } = routeValidation.data;

  // Callback to set the title based on loaded tasks
  const handleArtifactsLoaded = useCallback((artifacts: ListArtifactsResp) => {
    setTitle(artifacts.title);
  }, []);

  return (
    <ViewWrapper headerContent={<SiteHeaderContent includeBackButton={true} title={title} />}>
      <MainContentWrapper columns={7} className="gap-4 py-4">
        <MessageArtifactList
          threadId={threadId}
          messageId={messageId}
          onArtifactsLoaded={handleArtifactsLoaded}
        />
      </MainContentWrapper>
    </ViewWrapper>
  );
};
